import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CustomRolesService } from '../infrastructure/custom-roles.service';
import { QUERY_STALE_MS } from '@/shared/api/query-stale-time';
import type {
  CustomRoleDTO,
  CreateCustomRoleInput,
  UpdateCustomRoleInput,
} from '../infrastructure/custom-roles.service';

const customRolesService = new CustomRolesService();

export const customRoleKeys = {
  all: ['custom-roles'] as const,
};

export function useCustomRoles(
  cursor?: string,
  limit?: number,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [...customRoleKeys.all, { cursor, limit }],
    queryFn: () => customRolesService.list(cursor, limit),
    enabled: options?.enabled ?? true,
    staleTime: QUERY_STALE_MS.CATALOG,
  });
}

export function useCreateCustomRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomRoleInput) => customRolesService.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customRoleKeys.all });
    },
  });
}

export function useUpdateCustomRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCustomRoleInput }) =>
      customRolesService.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customRoleKeys.all });
    },
  });
}

export function useDeleteCustomRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => customRolesService.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customRoleKeys.all });
    },
  });
}

export type { CustomRoleDTO, CreateCustomRoleInput, UpdateCustomRoleInput };
